import { useEffect, useRef, useState } from "react";
import "./Style.css";
import SvgMapper from "./SvgMapper";
import useInterviewList from "../../hooks/useInterviewList";
import { renderDateTime } from "../../utility/common";
import { useDispatch } from "react-redux";
import {
  updateInterviewConfigData,
  setOpenModal,
  setInterviewList,
} from "../../../store/interviewConfigReducer";
import { trimToWordLimit } from "../../utility/common";
import { updateInterviewStatus } from "../../../application/services/interviewConfigService";


const difficultyColors = {
  beginner: "bg-green-50 text-green-600",
  intermediate: "bg-yellow-50 text-yellow-600",
  advanced: "bg-red-50 text-red-600",
};

const statusColors = {
  green: "bg-green-400",
  gray: "bg-gray-300",
};

const Accordion = ({ selectedTab }) => {
  const dispatch = useDispatch();
  const [openIndex, setOpenIndex] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const { interviewList, loadMore, hasMore, loading } = useInterviewList();
  const scrollRef = useRef(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      loadMore();
    }
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpenIndex(null);
  }, [selectedTab]);

  if (!interviewList || interviewList?.length === 0) {
    return (
      <div className="p-6 text-gray-500 italic">No interviews created yet.</div>
    );
  }

  const filteredList = interviewList?.filter((item) => {
    if (selectedTab === "All") {
      return true;
    }
    
    const categoryValue = item.categoryLookup?.id;
    return categoryValue === selectedTab;

  });

  if (filteredList.length === 0) {
  return (
    <div className="p-6 text-gray-500 italic">
      No interviews available for this category.
    </div>
  );
}

  const handleCloneInterview = (item) => {
    dispatch(
      updateInterviewConfigData({
        interviewName: item.interview_name,
        duration: item.duration,
        difficulty: item.difficulty,
        categoryName: item.categoryLookup?.value || "",
        categoryId: item.categoryLookup?.id || null,
        industryType: item.industryTypeLookup?.id || null,
        interviewRole: item.interviewRoleLookup?.id || null,
        jobDescription: item.job_description || "",
        rubric_url: item.rubric_url || "",
        additionalData: item.additionalData || "",
        interviewType: item.interviewTypeLookup?.id || "",
      }),
    );

    dispatch(setOpenModal(true));
  };

  const handleStatusToggle = async (item) => {
    const newStatus =
      item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      try {
        setUpdatingId(item.id);
        await updateInterviewStatus(item.id, newStatus);

        const updatedList = interviewList.map((i) =>
           i.id === item.id ? { ...i, status: newStatus } : i
        );

        dispatch(setInterviewList(updatedList));
      } catch (error) {
        console.error("Status update failed:", error);
      } finally {
        setUpdatingId(null);
      }
      };


  return (
    <div
      ref={scrollRef}
      className="space-y-4 max-h-[48rem] overflow-y-auto pr-2 custom-scrollbar"
    >
      {filteredList.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded-lg">
          <div
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => toggleAccordion(index)}
          >
            <div className="flex items-center space-x-4">
              {SvgMapper.dropdown(
                `w-5 h-5 text-gray-400 transform transition-transform duration-200 ${
                  openIndex === index ? "rotate-180" : ""
                }`
              )}

              <div>
                <h3 className="font-semibold text-gray-900">
                  {item.interview_name}
                </h3>
                <div className="flex items-center space-x-4 mt-1">
                  <span className="text-sm text-gray-500 flex items-center">
                    {SvgMapper.clock("w-4 h-4 mr-1")}
                    {item.duration} min
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      difficultyColors[item.difficulty?.toLowerCase()] ||
                      "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {item.difficulty &&
                      item.difficulty.charAt(0).toUpperCase() +
                        item.difficulty.slice(1).toLowerCase()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                className="px-3 py-1.5 text-sm border rounded-md bg-orange-600 hover:bg-orange-700 text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloneInterview(item);
                }}
              >
                Clone
              </button>
              <div
                className="flex items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={item.status === "ACTIVE"}
                    onChange={() => handleStatusToggle(item)}
                    disabled={updatingId === item.id}
                  />
                  <div className="w-9 h-5 bg-gray-300 rounded-full peer-checked:bg-green-500 transition-colors"></div>
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4"></div>
                </label>
              </div>
            </div>
          </div>
          {openIndex === index && (
            <div className="border-t border-gray-200 bg-gray-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start p-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Category
                  </h4>
                  <div className="space-y-1">
                    {item?.categoryLookup?.value ? (
                      <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full mr-2">
                        {item.categoryLookup.value}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No category selected
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Interview Type
                  </h4>
                  <div className="space-y-1">
                    {item?.interviewTypeLookup?.value ? (
                      <span className="inline-block bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded-full mr-2">
                        {item.interviewTypeLookup.value}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Interviewer Role
                  </h4>
                  <div className="space-y-1">
                    {item?.interviewRoleLookup?.value ? (
                      <span
                        className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-2"
                      >
                        {item.interviewRoleLookup.value}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No interviewer role selected
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Industry Type</h4>
                  <div className="space-y-1">
                    {item?.industryTypeLookup?.value ? (
                      <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full mr-2">
                        {item.industryTypeLookup.value}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No industry type selected
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Rubric File
                  </h4>
                  <div className="space-y-1">
                    {item?.rubric_url ? (
                      <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full mr-2">
                        <a
                          href={item.rubric_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Click to Download
                        </a>
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No rubric file uploaded
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Date & Time
                  </h4>
                  <div className="space-y-1">
                    {item?.created_at ? (
                      <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-2">
                        {renderDateTime(item.created_at)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No creation date available
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-400 mt-4 w-full"></div>
                <div className="p-4">
                  <h4 className="font-medium text-gray-900 mb-2 mt-2">
                    Job Description
                  </h4>
                  <div className="space-y-1">
                    {item?.job_description ? (
                      (() => {
                        const originalText = item.job_description;
                        const trimmedText = trimToWordLimit(originalText, 25);
                        const isTrimmed = trimmedText !== originalText;

                        return isTrimmed ? (
                          <div className="jd-tooltip">
                            <p className="text-sm text-gray-700 jd-clamp">
                              {trimmedText}
                            </p>

                            <div className="jd-tooltip-content">
                              {originalText}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700">
                            {originalText}
                          </p>
                        );
                      })()
                    ) : (
                      <span className="text-sm text-gray-500">
                        No job description provided
                      </span>
                    )}
                  </div>
                </div>
            </div>
          )}
        </div>
      ))}
      {loading && (
        <div className="text-center py-2 text-gray-500 text-sm">
          Loading more...
        </div>
      )}
      {!hasMore && (
        <div className="text-center py-2 text-gray-400 text-sm">
          No more interviews.
        </div>
      )}
    </div>
  );
};

export default Accordion;
